// font.ts ↔ game/font.cpp + the Font inlines (ht.h:1254+).
//
// A font is a JSON map ("glyph_map": char → glyph image name, + height/
// glyph_overlap/line_overlap and an optional "default" glyph) whose glyphs are
// atlas sprites (TBitmaps). Measurement (GetTextExtent/CalcBreak) is pure glyph
// arithmetic; DrawText positions glyph blits through the TBitmap/DibBitmap seam
// (glyphs are baked white-on-transparent; a requested color modulates via
// BltGlyphTinted — M2 pixel op, geometry identical).

import type { TBitmap } from './tbitmap.ts';
import type { TexAtlasMgr, DibBitmapLike, Color } from './texatlasmgr.ts';
import type { PackFileReader } from '../io/packfile.ts';

const IsBreakingChar = (ch: string): boolean => ch === ' ' || ch === '\t';

interface FontJson {
  glyph_map: Record<string, string>;
  default?: string;
  height: number;
  glyph_overlap: number;
  line_overlap: number;
}

// game/font.cpp:7-18
export function LoadFont(pakr: PackFileReader, tam: TexAtlasMgr, pszFont: string): Font | null {
  const pfnt = new Font(tam);
  return pfnt.Load(pakr, pszFont) ? pfnt : null;
}

export class Font {
  m_nGlyphOverlap = 0;
  m_nLineOverlap = 0;
  m_cxEllipsis = 0;
  m_cy = 0;
  m_ptbmDefault: TBitmap | null = null;
  private m_map = new Map<string, TBitmap>();
  private tam: TexAtlasMgr;

  constructor(tam: TexAtlasMgr) {
    this.tam = tam;
  }

  // game/font.cpp:37-104
  Load(pakr: PackFileReader, pszFont: string): boolean {
    const raw = pakr.MapFile(pszFont);
    if (raw === null) return false;
    const map = JSON.parse(new TextDecoder('latin1').decode(raw)) as FontJson;
    if (!map.glyph_map) return false;

    for (const [key, glyphFn] of Object.entries(map.glyph_map)) {
      // a glyph missing from the atlas is SKIPPED (never stored null);
      // GetTBitmap falls back to the space glyph for it (font.cpp:68-75)
      const ptbm = this.tam.CreateTBitmap(glyphFn);
      if (ptbm === null) continue;
      if (this.m_map.has(key)) return false;
      this.m_map.set(key, ptbm);
    }

    // optional default glyph (the shipped 2432 fonts omit it)
    if (map.default) {
      this.m_ptbmDefault = this.tam.CreateTBitmap(map.default);
    }

    this.m_cy = map.height;
    this.m_nGlyphOverlap = map.glyph_overlap;
    this.m_nLineOverlap = map.line_overlap;

    if (this.TBitmapExists('.')) this.m_cxEllipsis = this.GetTextExtent('...');
    return true;
  }

  // ht.h Font inlines
  GetHeight(): number {
    return this.m_cy;
  }
  GetGlyphOverlap(): number {
    return this.m_nGlyphOverlap;
  }
  GetLineOverlap(): number {
    return this.m_nLineOverlap;
  }

  // game/font.cpp:106-121
  GetTextExtent(psz: string, cch: number = psz.length): number {
    let width = 0;
    for (let i = 0; i < cch; i++) {
      const ptbm = this.GetTBitmap(psz[i]);
      if (ptbm === null) continue;
      width += ptbm.GetSize().cx - this.m_nGlyphOverlap;
    }
    return width;
  }

  // game/font.cpp:123-134 — counts every wrapped line (cursor-driven)
  CalcMultilineHeight(psz: string, cxMultiline: number): number {
    let cy = 0;
    let cur: string | null = psz;
    while (cur !== null) {
      const r = this.CalcBreak(cxMultiline, cur);
      cur = r.next;
      cy += this.m_cy - this.m_nLineOverlap;
    }
    return cy;
  }

  // game/font.cpp:136-157 — multiline draw with optional last-line ellipsis
  DrawTextMultiline(
    pbm: DibBitmapLike,
    psz: string,
    x: number,
    y: number,
    cx: number,
    cy: number,
    fEllipsis: boolean,
  ): void {
    const cyFont = this.m_cy - this.m_nLineOverlap;
    let cyT = cyFont;
    let cur: string | null = psz;
    while (cur !== null) {
      if (cy !== -1 && cyT > cy) return;
      const start: string = cur;
      const r = this.CalcBreak(cx, cur);
      cur = r.next;
      if (fEllipsis && cur !== null && cy !== -1 && cyT + cyFont > cy) {
        this.DrawTextWithEllipsis(pbm, start, r.cch, x, y, cx, true);
      } else if (fEllipsis && cur === null) {
        this.DrawTextWithEllipsis(pbm, start, r.cch, x, y, cx, false);
      } else {
        this.DrawText(pbm, start, x, y, r.cch);
      }
      y += cyFont;
      cyT += cyFont;
    }
  }

  // game/font.cpp:159-199 — binary-search the prefix that fits, append "..."
  DrawTextWithEllipsis(
    pbm: DibBitmapLike,
    psz: string,
    cch: number,
    x: number,
    y: number,
    cx: number,
    fForce: boolean,
  ): void {
    if (!fForce) {
      if (this.GetTextExtent(psz) < cx) {
        this.DrawText(pbm, psz, x, y, psz.length);
        return;
      }
    }
    let imin = 0;
    let imax = Math.min(cch - 1, 256 - 3 - 1); // the C++ szT[256] window
    if (imax < 0) imax = 0;
    let icur = imax;
    let ifit = imax;
    for (;;) {
      const icurT = imin + ((imax - imin) >> 1);
      if (icurT === icur) break;
      icur = icurT;
      const cxT = this.GetTextExtent(psz, icur + 1);
      if (cxT >= cx - this.m_cxEllipsis) {
        imax = icur - 1;
      } else {
        ifit = icur;
        imin = icur + 1;
      }
    }
    this.DrawText(pbm, psz.slice(0, ifit + 1) + '...', x, y, -1);
  }

  // game/font.cpp:203-211
  private FindNextNonBreakingChar(psz: string, i: number): number | null {
    while (i < psz.length) {
      if (!IsBreakingChar(psz[i])) return i;
      i++;
    }
    return null;
  }

  /** game/font.cpp:213-307 — line breaking. The C++ advances a char** cursor;
   *  here: returns the count of chars to draw on this line plus the REMAINDER
   *  string (null = done). */
  CalcBreak(cx: number, psz: string, fChop = true): { cch: number; next: string | null } {
    let pchBreakAfter: number | null = null;
    let cchBreak = 0;
    let cxT = 0;
    let pchT = 0;
    let cch = 0;
    let fFoundBreak = false;

    const remainder = (i: number | null): string | null => (i === null ? null : psz.slice(i));

    while (pchT < psz.length) {
      const ch = psz[pchT];

      // any combo of \n, \r, \n\r, \r\n ends the line
      if (ch === '\n') {
        pchT++;
        if (psz[pchT] === '\r') pchT++;
        return { cch, next: remainder(this.FindNextNonBreakingChar(psz, pchT)) };
      }
      if (ch === '\r') {
        pchT++;
        if (psz[pchT] === '\n') pchT++;
        return { cch, next: remainder(this.FindNextNonBreakingChar(psz, pchT)) };
      }

      if (IsBreakingChar(psz[pchT])) {
        fFoundBreak = true;
        pchBreakAfter = this.FindNextNonBreakingChar(psz, pchT);
        cchBreak = cch;
      }

      const ptbmChar = this.GetTBitmap(psz[pchT]);
      const cxChar = ptbmChar !== null ? ptbmChar.GetSize().cx - this.m_nGlyphOverlap : 0;
      if (cxT + cxChar > cx) {
        if (fFoundBreak) {
          return { cch: cchBreak, next: remainder(pchBreakAfter) };
        }
        if (!fChop) return { cch: 0, next: remainder(pchT) };
        // no break yet: scan forward for one so the next line has a start
        while (pchT < psz.length) {
          if (IsBreakingChar(psz[pchT])) {
            return { cch, next: remainder(this.FindNextNonBreakingChar(psz, pchT)) };
          }
          pchT++;
        }
        return { cch, next: null };
      }

      cxT += cxChar;
      cch++;
      pchT++;
    }
    return { cch, next: null };
  }

  /** game/font.cpp:309-345 — draw one run; returns the advance width
   *  (FancyText positions the next run from it). */
  DrawText(
    pbm: DibBitmapLike,
    psz: string,
    x: number,
    y: number,
    cch = -1,
    pclr: Color | null = null,
  ): number {
    if (cch === -1) cch = psz.length;
    let pos = 0;
    for (let i = 0; i < cch; i++) {
      const ptbm = this.GetTBitmap(psz[i]);
      if (ptbm === null) continue;
      if (pclr !== null) {
        this.tam.BltGlyphTinted(ptbm, pbm, x + pos, y, pclr);
      } else {
        ptbm.BltTo(pbm, x + pos, y);
      }
      pos = pos + ptbm.GetSize().cx - this.m_nGlyphOverlap;
    }
    return pos;
  }

  // game/font.cpp:347-363 — unmapped chars fall to default, then space, then null
  GetTBitmap(sz: string): TBitmap | null {
    const it = this.m_map.get(sz);
    if (it === undefined) {
      if (this.m_ptbmDefault !== null) return this.m_ptbmDefault;
      return this.m_map.get(' ') ?? null;
    }
    return it;
  }

  // game/font.cpp:365-370
  TBitmapExists(sz: string): boolean {
    return this.m_map.has(sz);
  }
}
